import QRCode from 'qrcode';
import {
  type ComputedRef,
  computed,
  nextTick,
  reactive,
  type ShallowRef,
  shallowRef,
} from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import {
  kioskRequiresPrivacyConsent,
  redirectToPrivacyConsentIfNeeded,
} from '@/components/steps/gdpr-consent/privacyConsent';
import { analytics } from '@/services/Analytics';
import { animations } from '@/services/Animations';
import { faceDetection } from '@/services/FaceDetection';
import { sentryService } from '@/services/telemetry/sentry';
import { describeError, track } from '@/shared/utils/track-telemetry';

export interface PhotoFromPhoneConsents {
  acceptResponsibility: boolean;
  agreePrivacy: boolean;
  allConsented: boolean;
  over16: boolean;
}

interface PulseAnimation {
  stopImmediately?: () => void;
}

interface PhoneUploadKioskConfig {
  hide_mobile_upload?: boolean;
  privacy_policy_variant?: string | null;
  show_privacy_popup?: boolean | null;
}

interface PhotoFromPhoneAnalytics {
  METRICS: {
    time_photo_selection_initiated: string;
    time_photo_selection_success: string;
    time_photo_upload: string;
  };
  trackTimeTo: (metric: string) => unknown;
}

interface PhotoFromPhoneRouter {
  query?: { from?: string };
  redirectToStep: (
    stepId: string | null,
    options?: {
      omitQuery?: boolean;
      query?: Record<string, string>;
    }
  ) => unknown;
}

export interface UsePhotoFromPhoneFlowOptions {
  getContinueButton: () => HTMLElement | null;
  getQrCodeContainer: () => HTMLElement | null;
}

export interface PhotoFromPhoneFlow {
  allGdprConsentsChecked: ComputedRef<boolean>;
  continueButtonPulseAnimation: ShallowRef<PulseAnimation | null | undefined>;
  disableSkipButton: ComputedRef<boolean>;
  enter: () => void;
  gdprConsents: PhotoFromPhoneConsents;
  generateQRCode: (link: string) => Promise<string>;
  getPhotoUploadLink: () => string;
  hasUploadedAndAnimatedPhotos: ShallowRef<boolean>;
  hasUploadedPhotos: ShallowRef<boolean>;
  hasWebcamPhotos: ComputedRef<boolean>;
  isUnmounted: ShallowRef<boolean>;
  leave: () => void;
  mountSessionId: ShallowRef<string | null | undefined>;
  onCancelUploadClick: () => void;
  onConfirmClick: () => void;
  onPhotoUpload: () => void;
  onPhotoUploadAndAnimationComplete: () => void;
  onPrivacyPolicyClick: () => void;
  onSkipKioskPhotoClick: () => void;
  pollingStartTimeout: ShallowRef<ReturnType<typeof setTimeout> | null>;
  qrCodeUrl: ShallowRef<string | null>;
  showBackToWebcamButton: ComputedRef<boolean>;
  showGdprCheckboxes: ComputedRef<boolean>;
}

function warn(context: string, error: unknown): void {
  // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
  console.warn(context, describeError(error));
}

function reportError(context: string, error: unknown): void {
  // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
  console.error(context, describeError(error));
}

export function usePhotoFromPhoneFlow({
  getContinueButton,
  getQrCodeContainer,
}: UsePhotoFromPhoneFlowOptions): PhotoFromPhoneFlow {
  const phoneAnalytics = analytics as unknown as PhotoFromPhoneAnalytics;
  const phoneRouter = router as unknown as PhotoFromPhoneRouter;
  const ui = useUiStore();
  const qrCodeUrl = shallowRef<string | null>(null);
  const hasUploadedPhotos = shallowRef(false);
  const hasUploadedAndAnimatedPhotos = shallowRef(false);
  const gdprConsents = reactive<PhotoFromPhoneConsents>({
    allConsented: false,
    over16: false,
    acceptResponsibility: false,
    agreePrivacy: false,
  });
  const continueButtonPulseAnimation = shallowRef<
    PulseAnimation | null | undefined
  >(null);
  const pollingStartTimeout = shallowRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isUnmounted = shallowRef(false);
  const mountSessionId = shallowRef<string | null | undefined>(null);

  const kioskConfig = (): PhoneUploadKioskConfig | null =>
    store.kiosk as PhoneUploadKioskConfig | null;

  const showGdprCheckboxes = computed((): boolean =>
    kioskRequiresPrivacyConsent(kioskConfig())
  );
  const allGdprConsentsChecked = computed(
    (): boolean =>
      !showGdprCheckboxes.value ||
      Object.values(gdprConsents).every((consent) => consent === true)
  );
  const disableSkipButton = computed((): boolean => hasUploadedPhotos.value);
  const showBackToWebcamButton = computed(
    (): boolean => phoneRouter.query?.from === 'webcam'
  );
  const hasWebcamPhotos = computed(
    (): boolean =>
      faceDetection.queryPhotos({ webcam: true, uploaded: true }).length > 0
  );

  function trackMounted(): void {
    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb('lifecycle', 'PhotoFromPhone mounted', {
          hasPrivacyPolicy: kioskRequiresPrivacyConsent(kioskConfig()),
          hideMobileUpload: kioskConfig()?.hide_mobile_upload,
          sessionId: store.sessionId,
        });
      },
      warn
    );
  }

  function redirectWhenPhoneUploadIsHidden(): boolean {
    if (kioskConfig()?.hide_mobile_upload !== true) {
      return false;
    }

    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'navigation',
          'Mobile upload disabled, redirecting to webcam'
        );
      },
      warn
    );
    phoneRouter.redirectToStep('photo-from-webcam');
    return true;
  }

  function getPhotoUploadLink(): string {
    let origin = `${window.location.protocol}//${window.location.host}`;
    if (origin === 'https://production.theartisan.ai') {
      origin = 'https://upload.theartisan.ai';
    }
    return `${origin}/customer/photo-from-phone?sessionId=${store.sessionId}`;
  }

  async function generateQRCode(link: string): Promise<string> {
    await nextTick();
    const size = getQrCodeContainer()?.clientWidth || 432;
    try {
      return await QRCode.toDataURL(link, {
        errorCorrectionLevel: 'H',
        type: 'image/png',
        quality: 1,
        margin: 3,
        width: size,
        height: size,
      });
    } catch (error) {
      track(
        '[Sentry] Failed to capture QR generation error:',
        () => {
          sentryService.captureException(error, {
            tags: {
              component: 'PhotoFromPhone',
              operation: 'qr_generation',
            },
            extra: {
              link_length: link?.length,
              container_width: size,
              error_message: describeError(error),
            },
          });
        },
        warn
      );
      ui.showError('photo_phone_qr_error', error);
      throw error;
    }
  }

  function startQrGeneration(): void {
    void generateQRCode(getPhotoUploadLink())
      .then((dataUrl) => {
        if (isUnmounted.value || store.sessionId !== mountSessionId.value) {
          return;
        }
        qrCodeUrl.value = dataUrl;
        track(
          '[Sentry] Tracking failed:',
          () => {
            sentryService.addBreadcrumb('ui', 'QR code generated successfully');
          },
          warn
        );
      })
      .catch((error) => {
        if (isUnmounted.value) {
          return;
        }
        reportError('[PhotoFromPhone] QR code generation failed:', error);
      });
  }

  function onPhotoUpload(): void {
    if (isUnmounted.value) {
      return;
    }
    hasUploadedPhotos.value = true;
    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'user_action',
          'Photo uploaded from mobile'
        );
      },
      warn
    );
  }

  function startPollingAfterDelay(): void {
    pollingStartTimeout.value = setTimeout(() => {
      pollingStartTimeout.value = null;
      if (isUnmounted.value || store.sessionId !== mountSessionId.value) {
        return;
      }
      void faceDetection.watchMobileUploads({
        preload: true,
        pollingInterval: 2000,
      });
      faceDetection.once(
        faceDetection.EVENTS.NEW_PHOTOS_UPLOADED,
        onPhotoUpload
      );
      track(
        '[Sentry] Tracking failed:',
        () => {
          sentryService.addBreadcrumb(
            'system',
            'Started watching mobile uploads',
            { pollingInterval: 2000 }
          );
        },
        warn
      );
    }, 1000);
  }

  function enter(): void {
    isUnmounted.value = false;
    mountSessionId.value = store.sessionId;
    trackMounted();

    if (
      redirectToPrivacyConsentIfNeeded(
        'photo-from-phone',
        { from: 'photo-from-phone' },
        warn
      ) ||
      redirectWhenPhoneUploadIsHidden()
    ) {
      return;
    }

    phoneAnalytics.trackTimeTo(
      phoneAnalytics.METRICS.time_photo_selection_initiated
    );

    if (!store.sessionId) {
      // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
      console.warn(
        '[PhotoFromPhone] No session ID available for QR code generation'
      );
      track(
        '[Sentry] Tracking failed:',
        () => {
          sentryService.addBreadcrumb(
            'ui',
            'QR code skipped: no session ID',
            {},
            'warning'
          );
        },
        warn
      );
      return;
    }

    startQrGeneration();
    startPollingAfterDelay();
  }

  function leave(): void {
    if (isUnmounted.value) {
      return;
    }
    isUnmounted.value = true;

    if (pollingStartTimeout.value) {
      clearTimeout(pollingStartTimeout.value);
      pollingStartTimeout.value = null;
    }

    faceDetection.unwatchMobileUploads();
    faceDetection.off(faceDetection.EVENTS.NEW_PHOTOS_UPLOADED, onPhotoUpload);
    continueButtonPulseAnimation.value?.stopImmediately?.();

    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb('lifecycle', 'PhotoFromPhone unmounted');
      },
      warn
    );
  }

  function onPrivacyPolicyClick(): void {
    ui.showPrivacyPolicyPopup = true;
    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'user_action',
          'Privacy policy popup opened'
        );
      },
      warn
    );
  }

  function onPhotoUploadAndAnimationComplete(): void {
    if (isUnmounted.value || hasUploadedAndAnimatedPhotos.value) {
      return;
    }
    hasUploadedAndAnimatedPhotos.value = true;
    phoneAnalytics.trackTimeTo(phoneAnalytics.METRICS.time_photo_upload);
    continueButtonPulseAnimation.value = (
      animations.pulseBox as unknown as (
        target: HTMLElement | null,
        options: { gap: number; infinite: boolean }
      ) => PulseAnimation | null | undefined
    )(getContinueButton(), { infinite: true, gap: 400 });
  }

  function onCancelUploadClick(): void {
    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'user_action',
          'User cancelled mobile upload',
          { nextStep: store.photoUploadNextStep }
        );
      },
      warn
    );
    void faceDetection
      .skipMobileUploads()
      .then(() => {
        if (isUnmounted.value) {
          return;
        }
        phoneRouter.redirectToStep(store.photoUploadNextStep);
      })
      .catch((error: unknown) => {
        warn('[PhotoFromPhone] skipMobileUploads failed:', error);
      });
  }

  function onSkipKioskPhotoClick(): void {
    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb('user_action', 'User skipped kiosk photo', {
          hasWebcamPhotos: hasWebcamPhotos.value,
          hasUploadedPhotos: hasUploadedAndAnimatedPhotos.value,
        });
      },
      warn
    );
    phoneAnalytics.trackTimeTo(
      phoneAnalytics.METRICS.time_photo_selection_success
    );
    phoneRouter.redirectToStep('faces');
  }

  function onConfirmClick(): void {
    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'user_action',
          'User confirmed photo selection',
          {
            nextStep: store.photoUploadNextStep,
            showBackToWebcamButton: showBackToWebcamButton.value,
            allGdprConsentsChecked: allGdprConsentsChecked.value,
          }
        );
      },
      warn
    );
    phoneAnalytics.trackTimeTo(
      phoneAnalytics.METRICS.time_photo_selection_success
    );

    const params = showBackToWebcamButton.value
      ? { query: { from: 'phone' } }
      : { omitQuery: true };
    phoneRouter.redirectToStep(store.photoUploadNextStep, params);
  }

  return {
    allGdprConsentsChecked,
    continueButtonPulseAnimation,
    disableSkipButton,
    enter,
    gdprConsents,
    generateQRCode,
    getPhotoUploadLink,
    hasUploadedAndAnimatedPhotos,
    hasUploadedPhotos,
    hasWebcamPhotos,
    isUnmounted,
    leave,
    mountSessionId,
    onCancelUploadClick,
    onConfirmClick,
    onPhotoUpload,
    onPhotoUploadAndAnimationComplete,
    onPrivacyPolicyClick,
    onSkipKioskPhotoClick,
    pollingStartTimeout,
    qrCodeUrl,
    showBackToWebcamButton,
    showGdprCheckboxes,
  };
}
